import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO, { SEOConfig } from '../components/SEO';
import {
  Shield, CheckCircle2, AlertTriangle, Calendar, FileText, Home,
  Scale, Clock, PoundSterling, Users, Heart, Wrench, Bell,
  ArrowRight, ChevronDown, ChevronUp, Sparkles, Lock, Zap
} from 'lucide-react';

const RRA_KEY_DATES = [
  { date: '1 May 2026', event: 'Act comes into force', status: 'upcoming', critical: true },
  { date: '1 May 2026', event: 'Section 21 abolished for new tenancies', status: 'upcoming', critical: true },
  { date: '1 May 2027', event: 'Section 21 abolished for ALL tenancies', status: 'future', critical: true },
  { date: '1 May 2026', event: 'Private Rented Sector Database launches', status: 'upcoming', critical: false },
  { date: '1 May 2026', event: 'New possession grounds take effect', status: 'upcoming', critical: false },
  { date: '1 May 2026', event: 'Pet request rights begin', status: 'upcoming', critical: false },
];

const COMPLIANCE_FEATURES = [
  {
    icon: Shield,
    title: 'Automated Compliance Checks',
    description: 'The system continuously monitors your properties for compliance issues before they become problems.',
    benefit: 'Never get caught out by expiring certificates'
  },
  {
    icon: FileText,
    title: 'Notice Generation',
    description: 'Generate legally compliant Section 8 notices with the correct grounds and notice periods.',
    benefit: 'Court-ready documentation every time'
  },
  {
    icon: Calendar,
    title: 'Rent Increase Management',
    description: 'Section 13 compliant rent increases with proper notice periods and tribunal-ready evidence.',
    benefit: '12 months between increases, automatic tracking'
  },
  {
    icon: Heart,
    title: 'Pet Request Handling',
    description: 'Structured pet request workflow with 28-day SLA tracking and insurance requirements.',
    benefit: 'Fair process for all parties'
  },
  {
    icon: Wrench,
    title: 'Repairs SLA Tracking',
    description: 'HHSRS-compliant repair management with automatic escalation and audit trails.',
    benefit: 'Category 1 hazards flagged immediately'
  },
  {
    icon: Scale,
    title: 'Ombudsman Ready',
    description: 'Case pack generation with chronological evidence for dispute resolution.',
    benefit: 'Be prepared, not surprised'
  },
];

const POSSESSION_GROUNDS = [
  { ground: 1, name: 'Landlord wishes to sell', notice: '4 months', protected: '12 months', new: false },
  { ground: '1A', name: 'Landlord/family occupation', notice: '4 months', protected: '12 months', new: true },
  { ground: 2, name: 'Mortgage repossession', notice: '2 months', protected: 'None', new: false },
  { ground: 6, name: 'Redevelopment', notice: '4 months', protected: '12 months', new: false },
  { ground: 8, name: 'Serious rent arrears (3 months)', notice: '4 weeks', protected: 'None', new: false },
  { ground: '8A', name: 'Repeated rent arrears', notice: '4 weeks', protected: 'None', new: true },
  { ground: 10, name: 'Some rent arrears', notice: '2 weeks', protected: 'None', new: false },
  { ground: 11, name: 'Persistent delay in paying rent', notice: '2 weeks', protected: 'None', new: false },
  { ground: 12, name: 'Breach of tenancy terms', notice: '2 weeks', protected: 'None', new: false },
  { ground: 14, name: 'Nuisance/antisocial behaviour', notice: 'Immediate', protected: 'None', new: false },
];

export default function RentersRightsActPage() {
  const [expandedSection, setExpandedSection] = useState(null);
  const [daysUntilAct, setDaysUntilAct] = useState(0);

  useEffect(() => {
    const actDate = new Date('2026-05-01');
    const today = new Date();
    const diffTime = actDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setDaysUntilAct(diffDays > 0 ? diffDays : 0);
  }, []);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="rra-info-page">
      <SEO {...SEOConfig.rentersRightsAct} />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
        
        <div className="max-w-6xl mx-auto px-6 py-16 relative">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-amber-500/20 text-amber-200 border-amber-400/30">
              <Bell className="w-3 h-3 mr-1" />
              Coming 1 May 2026
            </Badge>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Renters' Rights Act 2025
          </h1>
          <p className="text-xl text-emerald-100 mb-6 max-w-2xl">
            The biggest change to English private renting in a generation. 
            We're here to make compliance simple.
          </p>
          
          <div className="flex flex-wrap gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4 border border-white/20">
              <p className="text-3xl font-bold">{daysUntilAct}</p>
              <p className="text-emerald-200 text-sm">Days until Act</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4 border border-white/20">
              <p className="text-3xl font-bold">14</p>
              <p className="text-emerald-200 text-sm">Possession Grounds</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4 border border-white/20">
              <p className="text-3xl font-bold">28</p>
              <p className="text-emerald-200 text-sm">Days for Pet Decisions</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Link to="/register">
              <Button size="lg" className="bg-white text-emerald-900 hover:bg-emerald-50">
                <Sparkles className="w-4 h-4 mr-2" />
                Get Compliant with LetWise
              </Button>
            </Link>
            <Link to="/compliance">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                Check Your Compliance
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What's Changing Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">What's Changing?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The Renters' Rights Act 2025 transforms the private rented sector. Here's what you need to know.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Section 21 Abolition */}
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Section 21 Abolished</h3>
                <p className="text-slate-600 text-sm mb-3">
                  No-fault evictions end. New tenancies from May 2026, all tenancies from May 2027.
                </p>
                <Badge variant="destructive">Major Change</Badge>
              </CardContent>
            </Card>

            {/* Periodic Tenancies */}
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Periodic Tenancies Only</h3>
                <p className="text-slate-600 text-sm mb-3">
                  All ASTs become periodic from day one. Fixed terms no longer enforceable.
                </p>
                <Badge className="bg-blue-100 text-blue-700">Tenancy Structure</Badge>
              </CardContent>
            </Card>

            {/* Rent Increases */}
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                  <PoundSterling className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Rent Increase Rules</h3>
                <p className="text-slate-600 text-sm mb-3">
                  Maximum once per 12 months via Section 13. Tenants can challenge at tribunal.
                </p>
                <Badge className="bg-amber-100 text-amber-700">Financial</Badge>
              </CardContent>
            </Card>

            {/* Pet Rights */}
            <Card className="border-pink-200 bg-pink-50/50">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-pink-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Right to Request Pets</h3>
                <p className="text-slate-600 text-sm mb-3">
                  Tenants can request pets. Landlords must respond within 28 days with valid reasons.
                </p>
                <Badge className="bg-pink-100 text-pink-700">Tenant Rights</Badge>
              </CardContent>
            </Card>

            {/* PRS Database */}
            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">PRS Database</h3>
                <p className="text-slate-600 text-sm mb-3">
                  All landlords must register on the new Private Rented Sector Database.
                </p>
                <Badge className="bg-purple-100 text-purple-700">Registration</Badge>
              </CardContent>
            </Card>

            {/* Ombudsman */}
            <Card className="border-teal-200 bg-teal-50/50">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                  <Scale className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Mandatory Ombudsman</h3>
                <p className="text-slate-600 text-sm mb-3">
                  All landlords must join the PRS Ombudsman scheme for dispute resolution.
                </p>
                <Badge className="bg-teal-100 text-teal-700">Disputes</Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Key Dates Timeline */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Key Dates</h2>
          
          <div className="space-y-4">
            {RRA_KEY_DATES.map((item, idx) => (
              <div 
                key={idx} 
                className={`flex items-center gap-4 p-4 rounded-xl border ${
                  item.critical 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-white border-slate-200'
                }`}
              >
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                  item.critical ? 'bg-red-500' : 'bg-emerald-500'
                }`} />
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">{item.event}</p>
                  <p className="text-sm text-slate-500">{item.date}</p>
                </div>
                {item.critical && (
                  <Badge variant="destructive" className="flex-shrink-0">Critical</Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* New Possession Grounds */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div 
            className="flex items-center justify-between cursor-pointer mb-6"
            onClick={() => toggleSection('grounds')}
          >
            <h2 className="text-3xl font-bold text-slate-900">New Possession Grounds</h2>
            {expandedSection === 'grounds' ? (
              <ChevronUp className="w-6 h-6 text-slate-400" />
            ) : (
              <ChevronDown className="w-6 h-6 text-slate-400" />
            )}
          </div>
          
          <p className="text-slate-600 mb-6">
            With Section 21 gone, landlords must use Section 8 grounds. Here are the key grounds under the new Act:
          </p>
          
          {expandedSection === 'grounds' && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left p-3 font-semibold">Ground</th>
                    <th className="text-left p-3 font-semibold">Reason</th>
                    <th className="text-left p-3 font-semibold">Notice</th>
                    <th className="text-left p-3 font-semibold">Protection Period</th>
                  </tr>
                </thead>
                <tbody>
                  {POSSESSION_GROUNDS.map((ground, idx) => (
                    <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50">
                      <td className="p-3">
                        <span className="font-mono font-bold">Ground {ground.ground}</span>
                        {ground.new && <Badge className="ml-2 bg-emerald-100 text-emerald-700">New</Badge>}
                      </td>
                      <td className="p-3">{ground.name}</td>
                      <td className="p-3">{ground.notice}</td>
                      <td className="p-3">{ground.protected}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {expandedSection !== 'grounds' && (
            <Button variant="outline" onClick={() => toggleSection('grounds')}>
              View All 14 Grounds <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </section>

      {/* How LetWise Helps */}
      <section className="py-16 px-6 bg-emerald-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-emerald-700 text-emerald-100 mb-4">
              <Zap className="w-3 h-3 mr-1" />
              LetWise Compliance Suite
            </Badge>
            <h2 className="text-3xl font-bold mb-4">How We Make Compliance Easy</h2>
            <p className="text-emerald-200 max-w-2xl mx-auto">
              Don't navigate the new regulations alone. LetWise automates compliance so you can focus on being a great landlord.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {COMPLIANCE_FEATURES.map((feature, idx) => (
              <Card key={idx} className="bg-white/10 border-white/20 backdrop-blur-sm">
                <CardContent className="p-6">
                  <feature.icon className="w-10 h-10 text-emerald-300 mb-4" />
                  <h3 className="font-bold text-lg mb-2 text-white">{feature.title}</h3>
                  <p className="text-emerald-200 text-sm mb-3">{feature.description}</p>
                  <div className="flex items-center gap-2 text-emerald-300 text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>{feature.benefit}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/register">
              <Button size="lg" className="bg-white text-emerald-900 hover:bg-emerald-50">
                Start Your Free Trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Common Questions</h2>
          
          <div className="space-y-4">
            {[
              {
                q: "When does Section 21 actually end?",
                a: "For new tenancies created after 1 May 2026, Section 21 is not available. For existing tenancies, it ends 1 May 2027. After that, no landlord in England can use no-fault eviction."
              },
              {
                q: "Can I still evict problem tenants?",
                a: "Yes. Section 8 provides grounds for eviction including rent arrears (Ground 8/10/11), antisocial behaviour (Ground 14), breach of tenancy (Ground 12), and landlord sale/occupation (Ground 1/1A)."
              },
              {
                q: "What if a tenant requests a pet?",
                a: "You must respond within 28 days. You can only refuse on reasonable grounds (property unsuitable, lease restrictions). Pet insurance can be required. Silence equals consent."
              },
              {
                q: "How often can I increase rent?",
                a: "Maximum once every 12 months, using Section 13 procedure. Tenants can challenge at tribunal if they believe the increase exceeds market rate."
              },
              {
                q: "Do I need to register somewhere?",
                a: "Yes. The new Private Rented Sector Database launches May 2026. All landlords must register properties and join the PRS Ombudsman scheme."
              },
            ].map((faq, idx) => (
              <Card key={idx}>
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Lock className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h2 className="text-3xl font-bold mb-4">Stay Compliant, Stay Confident</h2>
          <p className="text-emerald-100 mb-8 max-w-xl mx-auto">
            The Renters' Rights Act doesn't have to be scary. With LetWise, you'll always know you're doing things right.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50">
                Get Started Free
              </Button>
            </Link>
            <Link to="/compliance">
              <Button size="lg" variant="outline" className="border-white/50 text-white hover:bg-white/10">
                Check Compliance Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
